/**
 * SectorHeatmap — 類股板塊熱力圖
 *
 * Two view modes:
 *   - matrix: rows = sectors, columns = 6 egg phases, cells = count + pct
 *   - phase:  6 columns (one per phase), sectors ranked by concentration %
 *
 * Accessibility & Quality:
 *   - All interactive cells are <button> with descriptive aria-labels
 *   - Full keyboard navigation (Tab / Enter / Space)
 *   - Text color chosen by computed WCAG contrast ratio against blended bg
 *   - prefers-reduced-motion respected on all animations
 *   - Zero-count cells rendered as disabled non-interactive items
 */

import React, { useCallback, useId, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  BarChart3,
  Columns,
  Crown,
  Flame,
  LayoutGrid,
  Layers,
  Maximize2,
  RefreshCw,
  Search,
  Snowflake,
  Sprout,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import {
  EGG_PHASES,
  PHASE_META,
  ZONE_META,
  type EggPhase,
  type PhaseZone,
  type SectorBreadthItem,
  type SectorPhaseFilter,
} from '../types';

/* ------------------------------ design tokens ------------------------------ */

type RGB = { r: number; g: number; b: number };

/** Zone base colors (Tailwind palette values, hard-coded for deterministic blending). */
const ZONE_COLORS: Record<PhaseZone, RGB> = {
  buy: { r: 16, g: 185, b: 129 }, // emerald-500
  wait: { r: 251, g: 191, b: 36 }, // amber-400
  sell: { r: 244, g: 63, b: 94 }, // rose-500
};

/** Panel background the cells are composited over (slate-900). */
const PANEL_BG: RGB = { r: 15, g: 23, b: 42 };

const TEXT_LIGHT: RGB = { r: 248, g: 250, b: 252 }; // slate-50
const TEXT_DARK: RGB = { r: 11, g: 18, b: 32 }; // near slate-950

/** Fixed saturation ceiling for percentage mode: >= 50% concentration = full intensity. */
const PCT_SCALE_MAX = 50;
/** Gamma applied to normalized intensity for better mid-range visibility. */
const INTENSITY_GAMMA = 0.7;
/** Minimum alpha so even 0-value cells carry a faint zone tint. */
const ALPHA_MIN = 0.12;
const ALPHA_SPAN = 1 - ALPHA_MIN;

const PHASE_ICONS: Record<EggPhase, typeof Snowflake> = {
  B3: Snowflake,
  A1: Sprout,
  A2: TrendingUp,
  A3: Flame,
  B1: TrendingDown,
  B2: TrendingDown,
};

/* --------------------------- color math (WCAG) ----------------------------- */

function blend(fg: RGB, bg: RGB, alpha: number): RGB {
  return {
    r: Math.round(fg.r * alpha + bg.r * (1 - alpha)),
    g: Math.round(fg.g * alpha + bg.g * (1 - alpha)),
    b: Math.round(fg.b * alpha + bg.b * (1 - alpha)),
  };
}

function channelLin(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }: RGB): number {
  return 0.2126 * channelLin(r) + 0.7152 * channelLin(g) + 0.0722 * channelLin(b);
}

function contrastRatio(l1: number, l2: number): number {
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Picks the text color (light or dark) with the higher WCAG contrast ratio
 * against the actual blended cell background. Guarantees >= 4.5:1 for cell text.
 */
function bestTextColor(bg: RGB): string {
  const bgL = relativeLuminance(bg);
  const lightC = contrastRatio(relativeLuminance(TEXT_LIGHT), bgL);
  const darkC = contrastRatio(relativeLuminance(TEXT_DARK), bgL);
  return lightC >= darkC ? '#f8fafc' : '#0b1220';
}

function toRgba({ r, g, b }: RGB, alpha: number): string {
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Normalized intensity in [0,1] from a raw value and its scale max. */
function intensityFor(value: number, scaleMax: number): number {
  if (scaleMax <= 0 || value <= 0) return 0;
  return Math.pow(Math.min(value / scaleMax, 1), INTENSITY_GAMMA);
}

/* --------------------------------- types ----------------------------------- */

export type SectorHeatmapView = 'matrix' | 'phase';
export type HeatmapMetric = 'pct' | 'count';
export type SectorSortKey = 'total' | 'name' | 'phase';

export interface SectorHeatmapProps {
  items: SectorBreadthItem[];
  loading?: boolean;
  error?: string | null;
  onCellClick?: (filter: SectorPhaseFilter) => void;
  /** Compact mode for the macro tab: no summary cards, top-8 rows, expand button. */
  compact?: boolean;
  initialView?: SectorHeatmapView;
  onExpand?: () => void;
  onRetry?: () => void;
  className?: string;
}

const COMPACT_ROW_LIMIT = 8;
const COMPACT_PHASE_LIMIT = 5;

/* -------------------------------- component -------------------------------- */

export function SectorHeatmap({
  items,
  loading = false,
  error = null,
  onCellClick,
  compact = false,
  initialView = 'matrix',
  onExpand,
  onRetry,
  className = '',
}: SectorHeatmapProps) {
  const [view, setView] = useState<SectorHeatmapView>(initialView);
  const [metric, setMetric] = useState<HeatmapMetric>('pct');
  const [sortKey, setSortKey] = useState<SectorSortKey>('total');
  const [sortPhase, setSortPhase] = useState<EggPhase>('A2');
  const [query, setQuery] = useState('');
  const baseId = useId();

  /* ------------------------------ derived data ----------------------------- */

  const filteredItems = useMemo(() => {
    const q = query.trim().normalize('NFKC').toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.sector.normalize('NFKC').toLowerCase().includes(q));
  }, [items, query]);

  const sortedItems = useMemo(() => {
    const arr = [...filteredItems];
    switch (sortKey) {
      case 'name':
        arr.sort((a, b) => a.sector.localeCompare(b.sector, 'zh-Hant'));
        break;
      case 'phase':
        arr.sort(
          (a, b) =>
            b.phase_pcts[sortPhase] - a.phase_pcts[sortPhase] ||
            b.total_stocks - a.total_stocks ||
            a.sector.localeCompare(b.sector, 'zh-Hant')
        );
        break;
      case 'total':
      default:
        arr.sort(
          (a, b) =>
            b.total_stocks - a.total_stocks ||
            a.sector.localeCompare(b.sector, 'zh-Hant')
        );
    }
    return arr;
  }, [filteredItems, sortKey, sortPhase]);

  const visibleItems = useMemo(
    () => (compact ? sortedItems.slice(0, COMPACT_ROW_LIMIT) : sortedItems),
    [sortedItems, compact]
  );

  /** Per-column max counts for count-mode intensity (comparable within a phase). */
  const columnMaxCounts = useMemo(() => {
    const max: Record<EggPhase, number> = { B3: 0, A1: 0, A2: 0, A3: 0, B1: 0, B2: 0 };
    for (const item of filteredItems) {
      for (const p of EGG_PHASES) {
        if (item.phase_counts[p] > max[p]) max[p] = item.phase_counts[p];
      }
    }
    return max;
  }, [filteredItems]);

  const summary = useMemo(() => {
    const totalStocks = filteredItems.reduce((s, i) => s + i.total_stocks, 0);
    let bull: SectorBreadthItem | null = null;
    let bear: SectorBreadthItem | null = null;
    for (const it of filteredItems) {
      if (
        !bull ||
        it.pct_buy_zone > bull.pct_buy_zone ||
        (it.pct_buy_zone === bull.pct_buy_zone && it.total_stocks > bull.total_stocks)
      ) {
        bull = it;
      }
      if (
        !bear ||
        it.pct_sell_zone > bear.pct_sell_zone ||
        (it.pct_sell_zone === bear.pct_sell_zone && it.total_stocks > bear.total_stocks)
      ) {
        bear = it;
      }
    }
    return { totalSectors: filteredItems.length, totalStocks, bull, bear };
  }, [filteredItems]);

  /* ------------------------------ cell styling ----------------------------- */

  const getCellStyle = useCallback(
    (item: SectorBreadthItem, phase: EggPhase) => {
      const zone = PHASE_META[phase].zone;
      const raw = metric === 'pct' ? item.phase_pcts[phase] : item.phase_counts[phase];
      const scaleMax = metric === 'pct' ? PCT_SCALE_MAX : columnMaxCounts[phase];
      const intensity = intensityFor(raw, scaleMax);
      const alpha = ALPHA_MIN + ALPHA_SPAN * intensity;
      const bg = blend(ZONE_COLORS[zone], PANEL_BG, alpha);
      return {
        backgroundColor: toRgba(bg, 1),
        color: bestTextColor(bg),
        boxShadow:
          intensity === 0
            ? `inset 0 0 0 1px ${toRgba(ZONE_COLORS[zone], 0.25)}`
            : undefined,
      };
    },
    [metric, columnMaxCounts]
  );

  const handleCellClick = useCallback(
    (item: SectorBreadthItem, phase: EggPhase) => {
      onCellClick?.({ sector: item.sector, phase });
    },
    [onCellClick]
  );

  const handleSortPhaseSelect = useCallback((phase: EggPhase) => {
    setSortKey('phase');
    setSortPhase(phase);
  }, []);

  /* -------------------------------- rendering ------------------------------ */

  if (loading) {
    return (
      <div
        className={`rounded-2xl border border-slate-800 bg-slate-900/60 p-6 ${className}`}
        aria-busy="true"
        aria-label="載入類股熱力圖中"
      >
        <div className="mb-4 h-8 w-48 animate-pulse rounded-lg bg-slate-800 motion-reduce:animate-none" />
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 28 }).map((_, i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-slate-800 motion-reduce:animate-none"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`rounded-2xl border border-rose-900/60 bg-rose-950/30 p-6 ${className}`}
        role="alert"
      >
        <div className="flex items-center gap-3 text-rose-300">
          <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="font-medium">無法載入類股板塊資料：{error}</p>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-rose-700 bg-rose-900/40 px-4 py-2 text-sm text-rose-200 transition-colors hover:bg-rose-900/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 motion-reduce:transition-none"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            重試
          </button>
        )}
      </div>
    );
  }

  const toolbarId = `${baseId}-toolbar`;

  return (
    <section
      className={`stitch-card rounded-xl p-4 sm:p-5 ${className}`}
      aria-labelledby={`${baseId}-title`}
    >
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 id={`${baseId}-title`} className="text-base sm:text-lg font-bold text-slate-100">
              類股板塊熱力圖
            </h2>
            <span className="text-xs font-mono text-sky-400/85">
              (Sector Cycle Breadth Matrix)
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-300/90">
            依雞蛋週期階段（<span className="text-sky-300 font-mono">B3 → A1 → A2 → A3 → B1 → B2</span>）檢視各產業板塊的股票分布
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* View toggle */}
          <div
            role="group"
            aria-label="檢視模式"
            className="inline-flex overflow-hidden rounded-lg border border-[#1e293b]/80 bg-[#0b0f17]"
          >
            <button
              type="button"
              data-testid="view-toggle-matrix"
              aria-pressed={view === 'matrix'}
              onClick={() => setView('matrix')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 motion-reduce:transition-none ${
                view === 'matrix'
                  ? 'bg-primary-container text-white font-semibold'
                  : 'bg-[#0b0f17] text-slate-300 hover:bg-[#131a26] hover:text-white'
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />
              矩陣熱力圖
            </button>
            <button
              type="button"
              data-testid="view-toggle-phase"
              aria-pressed={view === 'phase'}
              onClick={() => setView('phase')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 motion-reduce:transition-none ${
                view === 'phase'
                  ? 'bg-primary-container text-white font-semibold'
                  : 'bg-[#0b0f17] text-slate-300 hover:bg-[#131a26] hover:text-white'
              }`}
            >
              <Columns className="h-3.5 w-3.5" aria-hidden="true" />
              依階段分類看板
            </button>
          </div>
          {compact && onExpand && (
            <button
              type="button"
              data-testid="expand-heatmap-btn"
              onClick={onExpand}
              className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/35 bg-[#131a26] px-3 py-1.5 text-xs font-mono text-sky-300 transition-colors hover:bg-sky-950/60 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 motion-reduce:transition-none"
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
              展開完整熱力圖
            </button>
          )}
        </div>
      </div>

      {/* Summary telemetry cards (hidden in compact) */}
      {!compact && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <SummaryCard
            icon={<Layers className="h-4 w-4 text-sky-400" aria-hidden="true" />}
            label="總類股數"
            value={String(summary.totalSectors)}
            sub={query ? `搜尋結果 ${summary.totalSectors} / ${items.length}` : '涵蓋產業板塊'}
            tone="slate"
          />
          <SummaryCard
            icon={<BarChart3 className="h-4 w-4 text-sky-400" aria-hidden="true" />}
            label="總股票數"
            value={summary.totalStocks.toLocaleString('zh-Hant')}
            sub="納入統計個股"
            tone="slate"
          />
          <SummaryCard
            icon={<TrendingUp className="h-4 w-4 text-emerald-400" aria-hidden="true" />}
            label="領先多頭板塊"
            value={summary.bull?.sector ?? '—'}
            sub={summary.bull ? `買進區佔比 ${summary.bull.pct_buy_zone.toFixed(1)}%` : '無資料'}
            tone="emerald"
          />
          <SummaryCard
            icon={<TrendingDown className="h-4 w-4 text-rose-400" aria-hidden="true" />}
            label="領先空頭板塊"
            value={summary.bear?.sector ?? '—'}
            sub={summary.bear ? `賣出區佔比 ${summary.bear.pct_sell_zone.toFixed(1)}%` : '無資料'}
            tone="rose"
          />
        </div>
      )}

      {/* Toolbar: search + metric + sort */}
      <div id={toolbarId} className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-52 flex-1 sm:max-w-xs">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-400/80"
            aria-hidden="true"
          />
          <input
            type="search"
            data-testid="sector-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜尋類股，例如：半導體"
            aria-label="搜尋類股名稱"
            className="w-full rounded-lg border border-[#1e293b]/80 bg-[#0b0f17] py-1.5 pl-9 pr-8 text-xs font-mono text-slate-100 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          />
          {query && (
            <button
              type="button"
              data-testid="sector-search-clear"
              onClick={() => setQuery('')}
              aria-label="清除搜尋"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-[#131a26] hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Metric toggle */}
        <div
          role="group"
          aria-label="顯示指標"
          className="inline-flex overflow-hidden rounded-lg border border-[#1e293b]/80 bg-[#0b0f17]"
        >
          {(['pct', 'count'] as const).map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`metric-toggle-${m}`}
              aria-pressed={metric === m}
              onClick={() => setMetric(m)}
              className={`px-3 py-1.5 text-xs font-mono transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 motion-reduce:transition-none ${
                metric === m
                  ? 'bg-primary-container text-white font-semibold'
                  : 'bg-[#0b0f17] text-slate-300 hover:bg-[#131a26] hover:text-white'
              }`}
            >
              {m === 'pct' ? '百分比 %' : '股票檔數'}
            </button>
          ))}
        </div>

        {/* Sort controls */}
        <div className="flex items-center gap-2">
          <label htmlFor={`${baseId}-sort`} className="text-xs font-mono text-sky-400/85">
            排序
          </label>
          <select
            id={`${baseId}-sort`}
            data-testid="sector-sort-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SectorSortKey)}
            className="rounded-lg border border-[#1e293b]/80 bg-[#0b0f17] px-2.5 py-1.5 text-xs font-mono text-slate-100 focus:border-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          >
            <option value="total">總檔數（多→少）</option>
            <option value="name">類股名稱（A→Z）</option>
            <option value="phase">指定階段佔比</option>
          </select>
          {sortKey === 'phase' && (
            <select
              data-testid="sector-phase-select"
              value={sortPhase}
              onChange={(e) => setSortPhase(e.target.value as EggPhase)}
              aria-label="排序依據的階段"
              className="rounded-lg border border-[#1e293b]/80 bg-[#0b0f17] px-2.5 py-1.5 text-xs font-mono text-slate-100 focus:border-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              {EGG_PHASES.map((p) => (
                <option key={p} value={p}>
                  {PHASE_META[p].label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-300">
        <span className="text-sky-400/85 font-mono">色彩深度：</span>
        {(['buy', 'wait', 'sell'] as const).map((z) => (
          <span key={z} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-3 w-6 rounded-sm"
              style={{ backgroundColor: toRgba(ZONE_COLORS[z], 0.85) }}
            />
            {ZONE_META[z].label}
          </span>
        ))}
        <span className="text-slate-400">
          {metric === 'pct'
            ? '（百分比 0–50%+ 對應色深）'
            : '（各階段欄內相對最大檔數對應色深）'}
        </span>
      </div>

      {/* Empty state */}
      {visibleItems.length === 0 ? (
        <div
          data-testid="sector-empty-state"
          className="flex flex-col items-center gap-2 py-12 text-slate-400"
        >
          <Search className="h-8 w-8" aria-hidden="true" />
          <p>{query ? `找不到符合「${query}」的類股` : '尚無類股資料'}</p>
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-sm text-sky-400 underline hover:text-sky-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              清除搜尋條件
            </button>
          )}
        </div>
      ) : view === 'matrix' ? (
        <MatrixView
          items={visibleItems}
          metric={metric}
          sortKey={sortKey}
          sortPhase={sortPhase}
          baseId={baseId}
          getCellStyle={getCellStyle}
          onCellClick={handleCellClick}
          onSortPhaseSelect={handleSortPhaseSelect}
        />
      ) : (
        <PhaseView
          items={visibleItems}
          compact={compact}
          baseId={baseId}
          onCellClick={handleCellClick}
        />
      )}

      {compact && sortedItems.length > COMPACT_ROW_LIMIT && (
        <p className="mt-3 text-center text-xs font-mono text-sky-400/75">
          僅顯示前 {COMPACT_ROW_LIMIT} 大板塊，共 {sortedItems.length} 個
        </p>
      )}
    </section>
  );
}

/* ------------------------------ subcomponents ------------------------------ */

function SummaryCard({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: 'slate' | 'emerald' | 'rose';
}) {
  const toneRing =
    tone === 'emerald'
      ? 'border-emerald-500/35 bg-emerald-950/20'
      : tone === 'rose'
        ? 'border-rose-500/35 bg-rose-950/20'
        : 'border-[#182232] bg-[#0b0f17]/85';
  const toneText =
    tone === 'emerald'
      ? 'text-emerald-300'
      : tone === 'rose'
        ? 'text-rose-300'
        : 'text-slate-100';
  return (
    <div className={`rounded-xl border ${toneRing} p-3`}>
      <div className="mb-1 flex items-center gap-1.5 text-xs text-slate-300">
        {icon}
        <span>{label}</span>
      </div>
      <p className={`truncate text-base font-bold ${toneText}`} title={value}>
        {value}
      </p>
      <p className="truncate text-xs font-mono text-sky-400/80" title={sub}>
        {sub}
      </p>
    </div>
  );
}

/* ------------------------------- matrix view ------------------------------- */

interface MatrixViewProps {
  items: SectorBreadthItem[];
  metric: HeatmapMetric;
  sortKey: SectorSortKey;
  sortPhase: EggPhase;
  baseId: string;
  getCellStyle: (item: SectorBreadthItem, phase: EggPhase) => React.CSSProperties;
  onCellClick: (item: SectorBreadthItem, phase: EggPhase) => void;
  onSortPhaseSelect: (phase: EggPhase) => void;
}

function MatrixView({
  items,
  metric,
  sortKey,
  sortPhase,
  getCellStyle,
  onCellClick,
  onSortPhaseSelect,
}: MatrixViewProps) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[#182232]">
      <table data-testid="sector-matrix-table" className="w-full border-collapse text-sm">
        <caption className="sr-only">
          類股板塊熱力圖：每列為一個產業板塊，每欄為一個雞蛋週期階段，儲存格顯示股票檔數與佔比
        </caption>
        <thead>
          <tr className="bg-[#0b0f17] text-xs text-slate-200 border-b border-[#182232]">
            <th
              scope="col"
              className="sticky left-0 z-10 bg-[#0b0f17] px-3 py-2 text-left font-medium"
            >
              <span>類股板塊</span>{' '}
              <span className="text-[10px] font-mono text-sky-400/80">(Sector)</span>
            </th>
            {EGG_PHASES.map((p) => {
              const meta = PHASE_META[p];
              const isActiveSort = sortKey === 'phase' && sortPhase === p;
              return (
                <th key={p} scope="col" className="px-1 py-2 text-center">
                  <button
                    type="button"
                    data-testid={`sort-header-${p}`}
                    onClick={() => onSortPhaseSelect(p)}
                    aria-label={`依 ${meta.label} 佔比排序`}
                    aria-pressed={isActiveSort}
                    className={`inline-flex w-full flex-col items-center gap-0.5 rounded-lg px-2 py-1 transition-colors hover:bg-[#131a26] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 motion-reduce:transition-none ${
                      isActiveSort ? 'bg-[#131a26] text-white' : ''
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className="inline-block h-1.5 w-8 rounded-full"
                      style={{ backgroundColor: toRgba(ZONE_COLORS[meta.zone], 0.9) }}
                    />
                    <span className="font-bold">{p}</span>
                    <span className="text-[10px] text-sky-400/85">
                      {meta.zone === 'buy' ? '買' : meta.zone === 'wait' ? '觀' : '賣'}
                    </span>
                    {isActiveSort && <ArrowDown className="h-3 w-3" aria-hidden="true" />}
                  </button>
                </th>
              );
            })}
            <th scope="col" className="px-3 py-2 text-right font-medium">
              總檔數
            </th>
            <th scope="col" className="px-3 py-2 text-center font-medium">
              <span className="sr-only">區域分布</span>
              <span aria-hidden="true">區域分佈</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#182232]/70">
          {items.map((item) => (
            <tr
              key={item.sector}
              data-testid={`sector-row-${item.sector}`}
              className="hover:bg-[#131a26]/60 transition-colors"
            >
              <th
                scope="row"
                className="sticky left-0 z-10 bg-[#0e141f] px-3 py-2 text-left font-medium text-slate-200"
              >
                {item.sector}
              </th>
              {EGG_PHASES.map((p) => {
                const count = item.phase_counts[p];
                const pct = item.phase_pcts[p];
                const meta = PHASE_META[p];
                const style = getCellStyle(item, p);
                const isDominant = item.dominant_phase === p && count > 0;
                const display = metric === 'pct' ? `${pct.toFixed(1)}%` : String(count);

                if (count === 0) {
                  // Zero-count cells are non-interactive (filtering would yield nothing).
                  return (
                    <td key={p} className="px-1 py-1 text-center">
                      <span
                        data-testid={`cell-${item.sector}-${p}`}
                        className="inline-flex h-11 w-full items-center justify-center rounded-lg text-xs text-slate-600"
                        style={style}
                        aria-label={`${item.sector} ${meta.label}：無個股`}
                      >
                        –
                      </span>
                    </td>
                  );
                }

                return (
                  <td key={p} className="px-1 py-1">
                    <button
                      type="button"
                      data-testid={`cell-${item.sector}-${p}`}
                      onClick={() => onCellClick(item, p)}
                      style={style}
                      aria-label={`${item.sector} ${meta.label}：${count} 檔，佔 ${pct.toFixed(1)}%，點擊篩選選股雷達`}
                      className={`inline-flex h-11 w-full flex-col items-center justify-center rounded-lg px-1 transition-transform hover:scale-[1.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 motion-reduce:transition-none ${
                        isDominant ? 'ring-2 ring-white/50' : ''
                      }`}
                    >
                      <span className="text-sm font-bold leading-tight">{display}</span>
                      <span className="text-[10px] opacity-80 leading-tight">
                        {metric === 'pct' ? `${count}檔` : `${pct.toFixed(0)}%`}
                      </span>
                    </button>
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right font-semibold text-slate-200">
                {item.total_stocks.toLocaleString('zh-Hant')}
              </td>
              <td className="px-3 py-2">
                <ZoneBar item={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 3-segment stacked bar: buy / wait / sell zone percentages. */
function ZoneBar({ item }: { item: SectorBreadthItem }) {
  const segments: Array<{ zone: PhaseZone; pct: number }> = [
    { zone: 'buy', pct: item.pct_buy_zone },
    { zone: 'wait', pct: item.pct_wait_zone },
    { zone: 'sell', pct: item.pct_sell_zone },
  ];
  return (
    <div
      role="img"
      aria-label={`區域分布：買進區 ${item.pct_buy_zone.toFixed(1)}%，觀察區 ${item.pct_wait_zone.toFixed(1)}%，賣出區 ${item.pct_sell_zone.toFixed(1)}%`}
      className="flex h-2.5 w-24 overflow-hidden rounded-full border border-[#1e293b]"
    >
      {segments.map(({ zone, pct }) => (
        <span
          key={zone}
          className="h-full"
          style={{
            width: `${pct}%`,
            backgroundColor: toRgba(ZONE_COLORS[zone], 0.85),
          }}
        />
      ))}
    </div>
  );
}

/* -------------------------------- phase view ------------------------------- */

interface PhaseViewProps {
  items: SectorBreadthItem[];
  compact: boolean;
  baseId: string;
  onCellClick: (item: SectorBreadthItem, phase: EggPhase) => void;
}

function PhaseView({ items, compact, baseId, onCellClick }: PhaseViewProps) {
  const limit = compact ? COMPACT_PHASE_LIMIT : items.length;
  return (
    <div
      data-testid="sector-phase-board"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6"
    >
      {EGG_PHASES.map((p) => {
        const meta = PHASE_META[p];
        const zone = meta.zone;
        const ranked = items
          .filter((i) => i.phase_counts[p] > 0)
          .sort(
            (a, b) =>
              b.phase_pcts[p] - a.phase_pcts[p] ||
              b.phase_counts[p] - a.phase_counts[p] ||
              a.sector.localeCompare(b.sector, 'zh-Hant')
          )
          .slice(0, limit);
        const totalInPhase = items.reduce((s, i) => s + i.phase_counts[p], 0);
        const Icon = PHASE_ICONS[p];
        const accent =
          zone === 'buy'
            ? 'border-emerald-500/35'
            : zone === 'sell'
              ? 'border-rose-500/35'
              : 'border-amber-500/35';
        const accentText =
          zone === 'buy'
            ? 'text-emerald-300'
            : zone === 'sell'
              ? 'text-rose-300'
              : 'text-amber-300';
        const zoneTag =
          zone === 'buy' ? '左側布局' : zone === 'sell' ? '過熱風險' : '趨勢觀察';

        return (
          <section
            key={p}
            data-testid={`phase-column-${p}`}
            aria-labelledby={`${baseId}-phase-${p}`}
            className={`flex flex-col rounded-xl border ${accent} bg-[#0b0f17]/85`}
          >
            <header className="p-3 bg-[#131a26]/60 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3
                  id={`${baseId}-phase-${p}`}
                  className={`flex items-center gap-1.5 font-bold ${accentText}`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {meta.label}
                </h3>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${accentText} bg-[#0b0f17]/80 border ${accent}`}
                >
                  {zoneTag}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-300">
                <span className="text-sky-400/85">{ZONE_META[zone].label}</span>・{meta.hint}｜共 <strong className="text-slate-100">{totalInPhase}</strong> 檔
              </p>
            </header>
            <ol className="max-h-80 flex-1 divide-y divide-[#182232]/70 overflow-y-auto">
              {ranked.length === 0 ? (
                <li className="p-3 text-center text-xs text-slate-500">此階段無板塊</li>
              ) : (
                ranked.map((item, idx) => {
                  const pct = item.phase_pcts[p];
                  const count = item.phase_counts[p];
                  const isLeader = idx === 0;
                  return (
                    <li key={item.sector}>
                      <button
                        type="button"
                        data-testid={`phase-item-${p}-${item.sector}`}
                        onClick={() => onCellClick(item, p)}
                        aria-label={`${item.sector}，${meta.label} 佔比 ${pct.toFixed(1)}%，${count} 檔，點擊篩選選股雷達`}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-[#131a26] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400 motion-reduce:transition-none ${
                          isLeader ? 'bg-[#131a26]/60' : ''
                        }`}
                      >
                        <span
                          className={`w-5 shrink-0 text-center text-xs ${
                            isLeader ? 'text-amber-300' : 'text-slate-400'
                          }`}
                        >
                          {isLeader ? (
                            <Crown className="mx-auto h-3.5 w-3.5" aria-hidden="true" />
                          ) : (
                            idx + 1
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-slate-100">
                            {item.sector}
                          </span>
                          <span
                            aria-hidden="true"
                            className="mt-0.5 block h-1 w-full overflow-hidden rounded-full bg-[#131a26]"
                          >
                            <span
                              className="block h-full rounded-full"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: toRgba(ZONE_COLORS[zone], 0.9),
                              }}
                            />
                          </span>
                        </span>
                        <span className="shrink-0 text-right">
                          <span className={`block text-sm font-bold ${accentText}`}>
                            {pct.toFixed(1)}%
                          </span>
                          <span className="block text-[10px] text-slate-400">
                            {count} 檔
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

export default SectorHeatmap;
